/*
 * Get ready — `getready.list` (App-shell canon §4, FLEXIBLE).
 *
 * Transcribed from the canon §4.1–§4.3 + §1.3 DOM ids + §4 testid table; the
 * DC mock (App Shell.dc.html readyEvents view) is the styling reference.
 * Canon wins on conflict.
 *
 * DATA (Ruling A + C, BOARDROOM-2026-08-09-133534):
 *  - The SHIPPED list renders ONLY live data: the real events the signed-in
 *    participant holds a place in (my-api /me/holdings merged with box-office
 *    orders), with an honest empty state when there are none. The canon's
 *    5-event [demo] dataset (src/data/seed.ts) is a TEST FIXTURE only.
 *  - The server is the readiness AUTHORITY: step states come from the
 *    server's readiness result via the src/data/getReady.ts seam. No
 *    client-persisted completion flag exists — the retired
 *    markProfileComplete pattern is gone, and the previous client-side
 *    profile computation (participantConfig + computeReadySteps) is no
 *    longer consulted for display.
 *
 * TICKET VISIBILITY (assign-cascade fix, dispatch BOARDROOM-2026-08-11-081455):
 * a card appears here ONLY for events the account EXPLICITLY holds a place in —
 * my-api /me/holdings (a self-assign the buyer made via the assign flow, or an
 * invitee's claim). Get ready NEVER auto-claims purchased tickets: the previous
 * "lazily ensure a self-holder for every purchased ticket on open" loop
 * silently self-allocated all of a buyer's tickets (the 6-of-8-assigned-to-You
 * corruption Duncan hit) and has been REMOVED. A purchased-but-unassigned
 * ticket lives on the Orders screen until the buyer assigns it.
 *
 * PER-HOLDING CARDS (child-holder launch-gate slice): one card per HOLDING,
 * child seats included and labelled ("For {name}" off the holdings wire's
 * kind/holderName carry) — the old byEvent collapse hid one of a guardian's
 * own-seat + child-seat pair in the same event. Older my-api (no kind/
 * holderName) renders exactly the pre-slice self-subject cards.
 *
 * CARD IDENTITY (Duncan UI order BOARDROOM-135429, P1): EVERY card header
 * identifies WHICH ticket it is — "what am I signing in here for? Which
 * ticket is it? Maybe I am going to do one of the races but not the others."
 * Under the event name, every card renders (1) the TICKET TYPE line (the
 * holdings wire's ticketTypeName — e.g. "Singles Solo Pro"; absent ⇒ omitted
 * honestly) and (2) the "For {participant full name}" line — child seats keep
 * the wire's holderName; every OTHER holding is the caller's own seat and
 * shows the signed-in holder's OWN name from the verified session ID-token
 * claims (nameFromClaims — the SAME source as the waiver signature prefill).
 * A token with no name claims omits the For line honestly (never "For "
 * blank, never an invented name).
 *
 * TODO(V1.1 kids amendment §12.2): the fuller guardian card treatment (Junior
 * Fun Run pattern — "Mia's profile", guardian footer note, child wizard
 * routes) is deferred with the managed-child wizard.
 *
 * CHILD IDENTITY EVERYWHERE (Duncan rulings BOARDROOM-2026-09-02-150714,
 * items 2/3/5):
 *  - Item 2 — a child-assigned ticket is identified by the CHILD's name on
 *    every card. The wire only carries child identity on kind='child'
 *    holdings; a child ticket whose SERVER holder is invitee/claimed (farmed
 *    to a parent email, claimed pre-slice) or kind-absent carries none — the
 *    LOCAL assign-store record does (localHolderOverlay resolution, matched
 *    by ticket row id). Precedence per ruling: wire holderName (kind='child')
 *    > local child-record name > session self-name — and the guardian's name
 *    NEVER renders on a ticket locally recorded as a child's (an unnamed
 *    child record omits the For line rather than fall back to the guardian).
 *    The "c/o {adult}" form is rendered NOWHERE here: every surface below is
 *    the owner's own account, where the adult name is superfluous (ruling).
 *  - Item 3 — Edit follows the ticket holder: the profile/emergency steps on
 *    a child-subject card open THAT CHILD's managed wizard
 *    (/profile/children/{childKey}/step-N — the same path Profile's managed
 *    list uses), never the owner's wizard. A child-subject card with NO
 *    local childKey routes to /profile (the managed list) — still never the
 *    owner's wizard.
 *  - Item 5 — a seat farmed to the child's own parent (guestparent record;
 *    server holder = invitee at the parent's email) renders a buyer-side
 *    WAITING card: visible, read-only, no CTAs. State is the allocations
 *    wire's honest holder.state ('invited' → waiting to accept; 'claimed' →
 *    in the parent's hands; ready/consumed → done). Completion beyond that
 *    is NOT on any buyer-readable wire — never fabricated.
 */
import { createContext, useContext, useEffect, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { LiveDataNotice, useData } from '../data/DataProvider'
import { NotWired } from '../components/NotWired'
import { useShellChrome } from '../components/Shell'
import { loadSession, nameFromClaims } from '../auth/session'
import { startSignIn } from '../auth/startSignIn'
import {
  applyDeclaredPermissionChild, applyGuardianClaim, applyProfileMismatch, cardProgress, cardState, compareGetReadyEvents,
  detectProfileMismatch, eventEnded, fetchEventSteps, heatIssuanceNote,
  heatPolicyFromReadiness, nextUpLine, nowStepCount, profileBlockCopy,
  profileEligibilityRejection, statusPill, stepEntryPath, stepsFromTicketReadiness,
  subscribeReadinessRefresh, visibleGetReadyEvents, withQuestionnaireStep,
  type GetReadyEvent, type GetReadyStep, type ProfileMismatchAttrs,
} from '../data/getReady'
import { guardianClaimForTicket } from '../data/guardianClaim'
import { EligibilityMismatchNotice } from '../components/EligibilityMismatchNotice'
import { useLocale, type MessageKey } from '../i18n/useLocale'
import { fetchAllReadiness, fetchHoldingsRead, fetchQuestionnaire, MyApiError, myApiBase, type Holding, type QuestionnaireStatus, type Readiness, type RemovedHolding } from '../data/myApi'
import { resolveLocalSeatRecords, type ResolvedLocalRecords } from './orders/localHolderOverlay'
import { getChildProfile, listChildren, subscribeAssignStore } from './orders/assignStore'
import { DEFAULT_WIZARD_CONFIG } from './wizard/wizardConfig'
import { allValid, dobIso, firstIncompleteStep, EMPTY_DRAFT } from './wizard/wizardModel'
import { loadIdentityVerificationLevel, loadSelfDraft } from './wizard/profileStore'
import { HeatSelect } from './HeatSelect'
import { ChildConfirm } from './ChildConfirm'
import { QuestionnaireFlow } from './questionnaire/QuestionnaireFlow'

// ---------------------------------------------------------------------------
// Step row — V1.9.1 measured geometry (design handoff README §Screens →
// 1. Get ready "Step row geometry"; App Shell.dc.html is the measured DOM
// reference). PRESENTATION ONLY — the step data/state machine is unchanged.
// ---------------------------------------------------------------------------

/** Mobile meta shorthands (README table — desktop always shows the full
 *  string). `Required by {org unit}` is deliberately ABSENT: the README
 *  forbids shortening it (dropping the prefix would leave the meta a bare
 *  restatement of the card kicker). Metas are never truncated — on mobile the
 *  meta is `white-space: nowrap` and drops WHOLE to the next line instead. */
const MOBILE_META_RULES: Array<[RegExp, string]> = [
  [/^You fill this in for .+$/, 'You fill in'],
  [/^You sign for .+$/, 'You sign'],
  [/^Choose your start time$/, 'Pick a time'],
  [/^After you pick a heat$/, 'After heat'],
  [/^After heats are allocated$/, 'After alloc.'],
  [/^Needed for your bib$/, 'For your bib'],
  [/^Still needs your (.+)$/, 'Needs $1'],
  [/^Optional but recommended$/, 'Optional'],
]
export function mobileMeta(full: string): string {
  for (const [re, out] of MOBILE_META_RULES) if (re.test(full)) return full.replace(re, out)
  return full
}

function StepRowView({ event, step, onAction }: {
  event: GetReadyEvent
  step: GetReadyStep
  onAction?: (event: GetReadyEvent, step: GetReadyStep) => void
}) {
  const { isMobile } = useShellChrome()
  const { t } = useLocale()
  const done = step.state === 'done'
  const soon = step.state === 'soon'
  // POST-EVENT CLOSE (BOARDROOM-2026-08-25-044751): once the event is ENDED —
  // the SERVER's eventClosed verdict OR past the shared partition boundary
  // (eventEnded composes both, past-stubs hardening CMT-2026-09-03-025 /
  // BOARDROOM-2026-09-03-060052) — the profile/waiver/heat steps show a
  // closed notice INSTEAD of their CTAs: no Start/Sign/Choose (the server
  // 409s event_ended on those writes), no amber "act now" styling — an
  // honest ended state. Unknown end date + no close signal ⇒ NOT ended
  // (never wrongly lock a live event — dispatch degrade item 3).
  const closed = eventEnded(event)
  const now = step.state === 'now' && !closed
  // ELIGIBILITY MISMATCH (BOARDROOM-2026-08-29-120259 ruling 3): the truthful
  // "complete — not valid for this ticket" overlay state. Amber attention
  // treatment WITHOUT the to-do semantics (never counted in the pill), and the
  // outline Edit keeps the wizard reachable. A closed event's archived read
  // wins (the step falls through to the closed notice like any non-done row).
  const blocked = step.state === 'blocked' && !closed

  // V1.9.1: when everything is complete the FINAL action on the system
  // Ticket-issued row reads `Issued` and routes to Tickets (README §Get
  // ready three-states). Suppressed on a closed card, which drops every
  // action (BOARDROOM-2026-08-25-044751 archived read).
  const issuedAction = !closed && step.key === 'ticket' && !!step.system && done

  // Action column: CTA when the step has a wired flow (`now` → solid teal;
  // `done` → outlined completed-but-editable); mono tag box otherwise for
  // soon/system rows. System steps never get a participant CTA (canon §4.3)
  // — the Issued terminal link above is a wallet route, not a step action.
  // A CLOSED event's non-system rows swap the CTA for the ended notice tag.
  const showCta = !closed && !step.system && !!step.cta && (now || done || blocked)
  const hasTag = !issuedAction
    && ((soon && !step.system) || !!step.system || (closed && !step.system))
  const tagText = closed && !step.system
    ? (isMobile ? t('getReady.tag.ended.mobile') : t('getReady.tag.ended.desktop'))
    : step.system
      ? (isMobile ? t('getReady.tag.system.mobile') : t('getReady.tag.system.desktop'))
      : (isMobile ? t('getReady.tag.soon.mobile') : t('getReady.tag.soon.desktop'))
  // Closed + not done → the row meta carries the closed notice (the step can
  // no longer be completed); done steps keep their honest Completed/Signed
  // meta. Mobile applies the README shorthand table to the data meta only.
  const metaText = closed && !step.system && !done
    ? t('getReady.meta.closed')
    : isMobile ? mobileMeta(step.meta) : step.meta

  // ONE grid per breakpoint for every row state (measured): mobile
  // `6px 20px minmax(0,1fr)` colgap 6 rowgap 6 pad 9px 6px, action on its own
  // second grid row; desktop `14px 24px minmax(0,1fr) auto` colgap 10 pad
  // 8px 12px, action in col 4.
  const rowStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: isMobile ? '6px 20px minmax(0,1fr)' : '14px 24px minmax(0,1fr) auto',
    columnGap: isMobile ? 6 : 10,
    ...(isMobile ? { rowGap: 6 } : {}),
    alignItems: 'center',
    padding: isMobile ? '9px 6px' : '8px 12px',
    borderBlockStart: '1px solid var(--tru-hairline-05)',
    // Actionable rows get the amber row tint (measured rgba(230,193,90,.05)).
    background: now ? 'var(--tru-status-amber-bg-05)' : undefined,
  }
  // col 1 connector — 6/14 × 2px bar coloured by the STEP's own state (teal
  // done / amber now / white .2 later), radius 0 2px 2px 0 written as logical
  // corners so it mirrors under RTL. `now` (closed suppresses amber) drives
  // the colour, matching the icon + meta emphasis.
  const connStyle: CSSProperties = {
    gridColumn: 1, width: isMobile ? 6 : 14, height: 2,
    borderStartStartRadius: 0, borderStartEndRadius: 2, borderEndEndRadius: 2, borderEndStartRadius: 0,
    background: done ? 'var(--tru-status-teal)' : now || blocked ? 'var(--tru-status-amber)' : 'rgba(255,255,255,.2)',
    opacity: soon ? 0.5 : 0.95,
  }
  // col 2 icon — 20/24px circle: ✓ teal-tint done, ! amber-tint actionable,
  // ○ neutral not-yet.
  const iconStyle: CSSProperties = {
    gridColumn: 2, width: isMobile ? 20 : 24, height: isMobile ? 20 : 24, borderRadius: '50%',
    display: 'grid', placeItems: 'center', fontSize: isMobile ? 11 : 12, fontWeight: 800,
    ...(done
      ? { background: 'var(--tru-tint-teal-20)', color: 'var(--tru-status-done-text)' }
      : now || blocked
        ? { background: 'var(--tru-tint-amber-20)', color: 'var(--tru-status-amber)' }
        : { background: 'rgba(255,255,255,.06)', color: 'var(--tru-text-muted)' }),
  }
  // col 3 text cell — label + meta share ONE baseline-aligned line; a long
  // meta drops WHOLE to a second line (nowrap on mobile), never truncates
  // (README: metas carry operational data — "Wave 3 · 07:40").
  const textCellStyle: CSSProperties = {
    gridColumn: 3, ...(isMobile ? { gridRow: 1 } : {}), minWidth: 0,
    display: 'flex', flexWrap: 'wrap', alignItems: 'baseline',
    gap: isMobile ? '2px 6px' : '4px 8px',
  }
  const labelStyle: CSSProperties = {
    fontSize: 13.5, fontWeight: 700,
    ...(isMobile ? { flex: 'none', whiteSpace: 'nowrap' } : {}),
    color: soon ? 'var(--tru-text-tertiary)' : 'var(--tru-text-primary)',
  }
  const metaStyle: CSSProperties = {
    fontSize: 11.5,
    ...(isMobile ? { whiteSpace: 'nowrap' } : {}),
    ...(now || blocked ? { color: 'var(--tru-status-amber)', fontWeight: 600 } : { color: 'var(--tru-text-quaternary)' }),
  }
  // Action placement: mobile → its own grid row, end-aligned; desktop → col 4.
  const actionPlacement: CSSProperties = isMobile
    ? { gridColumn: '1 / -1', gridRow: 2, justifySelf: 'end' }
    : { gridColumn: 4 }
  // Actionable = solid teal on #03201c; completed-but-editable = outlined.
  // Fixed 104/84px width, min-height 38/40 (measured).
  const ctaStyle: CSSProperties = {
    ...actionPlacement,
    boxSizing: 'border-box', width: isMobile ? 84 : 104, padding: '0 4px',
    minHeight: isMobile ? 40 : 38, borderRadius: 'var(--tru-r-btn)',
    fontSize: 12.5, fontWeight: 800, fontFamily: 'inherit', textAlign: 'center', cursor: 'pointer',
    // Blocked shares the outlined completed-but-editable voice: the data IS
    // complete; editing is optional, support is the fix (ruling 2).
    ...(done || blocked
      ? { border: '1px solid var(--tru-border-16)', background: 'transparent', color: '#c3c9c5' }
      : { border: 0, background: 'var(--tru-status-teal)', color: 'var(--tru-accent-ink)' }),
  }
  // Not-yet = mono tag box, 104/60px wide.
  const tagStyle: CSSProperties = {
    ...actionPlacement,
    boxSizing: 'border-box', width: isMobile ? 60 : 104, textAlign: 'center',
    fontSize: 9, letterSpacing: '.08em', color: 'var(--tru-text-muted)',
    border: '1px solid var(--tru-border-12)', borderRadius: 5, padding: '5px 4px',
  }

  return (
    <div id={`getready.eventcard.${event.id}.steps.${step.key}`} data-testid={`getready-step-${step.key}`} style={rowStyle}>
      <span aria-hidden style={connStyle} />
      <span data-testid="getready-step-icon" style={iconStyle}>{done ? '✓' : now || blocked ? '!' : '○'}</span>
      <div style={textCellStyle}>
        <span data-testid="getready-step-label" style={labelStyle}>{step.label}</span>
        <span data-testid="getready-step-meta" style={metaStyle}>{metaText}</span>
      </div>
      {showCta && (
        <button
          data-testid="getready-step-cta"
          onClick={() => onAction?.(event, step)}
          style={ctaStyle}
        >
          {/* CTA label = locale copy keyed by the state-machine value. */}
          {t(`getReady.cta.${step.cta}` as MessageKey)}
        </button>
      )}
      {issuedAction && (
        <Link
          data-testid="getready-step-issued"
          to="/tickets"
          style={{
            ...ctaStyle, textDecoration: 'none',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid var(--tru-border-16)', background: 'transparent', color: '#c3c9c5',
          }}
        >
          {t('getReady.issued.action')}
        </Link>
      )}
      {!showCta && !issuedAction && hasTag && (
        <span
          data-testid={closed && !step.system ? 'getready-step-closed' : 'getready-step-tag'}
          className="mono"
          style={tagStyle}
        >
          {tagText}
        </span>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Event card — V1.9.1 slate boundary + status bracket (design handoff README
// §Container boundary + §Screens → 1. Get ready). Presentation only.
// ---------------------------------------------------------------------------

function EventCard({ event, ticketId, onAction }: {
  event: GetReadyEvent
  /** The held ticket backing this card — the support pre-fill + reported-state
   *  key (per-ticket by design: a swapped ticket starts clean). Optional so
   *  fixture-driven unit renders (seed) need no ticket. */
  ticketId?: string
  onAction?: (event: GetReadyEvent, step: GetReadyStep) => void
}) {
  const { isMobile } = useShellChrome()
  const { t } = useLocale()

  // PARENT-SIGNING waiting card (ruling item 5): visible, NEVER actionable.
  // Renders the identity header (event · type · "For {child}") + the honest
  // wire-state line — no steps, no CTAs, no pill/progress (the buyer cannot
  // read the parent's readiness; nothing is fabricated). Placed before the
  // step-machine derivations, which have no meaning for a step-less card.
  if (event.waiting) {
    const parent = event.waiting.parentName || event.waiting.parentEmail
    const doneState = event.waiting.state === 'done'
    const text = parent
      ? t(`getReady.waiting.${event.waiting.state}` as MessageKey, { parent })
      : t(`getReady.waiting.${event.waiting.state}NoName` as MessageKey)
    return (
      <div
        id={`getready.eventcard.${event.id}`}
        data-testid="getready-eventcard"
        data-event-id={event.id}
        data-waiting={event.waiting.state}
        style={{
          background: 'var(--tru-surface-2)',
          border: 'var(--tru-slate-border)',
          borderRadius: 'var(--tru-slate-radius)',
          overflow: 'hidden',
          marginBlockEnd: 'var(--tru-card-gap)',
          boxShadow: 'var(--tru-slate-halo), var(--tru-slate-shadow)',
        }}
      >
        <div style={{ padding: '14px 16px', background: 'var(--tru-slate-header-fill)', borderBlockEnd: 'var(--tru-slate-header-rule)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {event.organiser.name && (
                <div className="mono" style={{ fontSize: 9.5, letterSpacing: '.11em', textTransform: 'uppercase', color: event.organiser.accentFg || 'var(--tru-text-tertiary)', marginBlockEnd: 3 }}>
                  {event.organiser.name}
                </div>
              )}
              <div style={{ fontSize: 14.5, fontWeight: 800, lineHeight: 1.2 }}>{event.name}</div>
              {event.ticketTypeName && (
                <div data-testid="getready-card-tickettype" style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--tru-text-secondary)', marginBlockStart: 2 }}>
                  {event.ticketTypeName}
                </div>
              )}
              {/* Subject = the CHILD (item 2); omitted when unnamed. */}
              {event.holderName && (
                <div data-testid="getready-card-subject" style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--tru-status-teal-bright)', marginBlockStart: 2 }}>
                  {t('getReady.card.forName', { name: event.holderName })}
                </div>
              )}
              {event.when && <div style={{ fontSize: 11.5, color: 'var(--tru-text-tertiary)' }}>{event.when}</div>}
            </div>
            {/* Mono read-only marker in place of the status pill. */}
            <span
              data-testid="getready-card-waiting-tag"
              className="mono"
              style={{ flex: 'none', fontSize: 9, letterSpacing: '.08em', color: 'var(--tru-text-muted)', border: '1px solid var(--tru-border-12)', borderRadius: 5, padding: '5px 8px' }}
            >
              {t('getReady.waiting.tag')}
            </span>
          </div>
        </div>
        {/* The honest wire-state line — dimmed teal when done, muted while
            pending; role=status, never a CTA anywhere on this card. */}
        <div
          data-testid="getready-card-waiting"
          data-state={event.waiting.state}
          role="status"
          style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '13px 16px',
            ...(doneState
              ? { borderBlockStart: '1px solid rgba(18,196,172,.28)', background: 'rgba(18,196,172,.09)' }
              : { borderBlockStart: '1px solid var(--tru-hairline-06)', opacity: 0.75 }),
          }}
        >
          <span aria-hidden style={{ flex: 'none', fontSize: 13, color: doneState ? 'var(--tru-status-done-text)' : 'var(--tru-text-tertiary)' }}>
            {doneState ? '✓' : '⧗'}
          </span>
          <span style={{ fontSize: 12.5, lineHeight: 1.45, fontWeight: 600, color: doneState ? 'var(--tru-status-done-text)' : 'var(--tru-text-secondary)' }}>
            {text}
          </span>
        </div>
      </div>
    )
  }

  const state = cardState(event)
  const pill = statusPill(event, t)
  const { doneN, yoursN, pct } = cardProgress(event)
  // POST-EVENT CLOSE (BOARDROOM-2026-08-25-044751) + past-stubs composition
  // (CMT-2026-09-03-025): ENDED = the server close flag OR past the shared
  // partition boundary (eventEnded) — the card dims, wears an "Event ended"
  // badge in place of the status pill, and drops the next-up / issuance
  // coaching lines (nothing is actionable).
  const closed = eventEnded(event)
  const nextUp = closed ? null : nextUpLine(event, t)
  // Organiser accent is DATA per org unit (central asset store) with a
  // graceful NEUTRAL fallback when the wire carries none — never a hardcoded
  // per-event colour, and never a status hue (README §Status colours).
  const kickerColor = event.organiser.accentFg || 'var(--tru-text-tertiary)'
  // Bracket colour is STATUS-only (QA 2.6): amber while anything is
  // actionable, teal otherwise (complete / nothing actionable / closed).
  const bracketColor = nowStepCount(event) > 0 ? 'var(--tru-status-amber)' : 'var(--tru-status-teal)'

  // Status chip — 10.5px/800, radius 20, amber `! N things to do` / teal ✓.
  const pillStyle: CSSProperties = {
    flex: 'none', display: 'inline-flex', alignItems: 'center', gap: 5,
    borderRadius: 20, padding: '3px 9px', fontSize: 10.5, fontWeight: 800,
    ...(pill.tone === 'warn'
      ? { background: 'rgba(230,193,90,.16)', color: 'var(--tru-status-amber)' }
      : { background: 'rgba(18,196,172,.16)', color: 'var(--tru-status-done-text)' }),
  }
  const pillNode = closed ? (
    <span
      data-testid="getready-card-ended"
      style={{
        ...pillStyle,
        background: 'rgba(255,255,255,.08)', color: 'var(--tru-text-secondary)',
        border: '1px solid var(--tru-border-12)',
      }}
    >{t('getReady.card.ended')}</span>
  ) : (
    <span data-testid="getready-card-status" style={pillStyle}>{`${pill.icon} ${pill.text}`}</span>
  )

  // Teal band shared by the "You're up to date" and "You're all set" states
  // (README "Three states, not two").
  const bandStyle: CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 11, padding: '13px 16px',
    borderBlockStart: '1px solid rgba(18,196,172,.28)', background: 'rgba(18,196,172,.09)',
  }
  const bandTickStyle: CSSProperties = {
    width: 26, height: 26, flex: 'none', borderRadius: '50%',
    background: 'var(--tru-status-teal)', color: 'var(--tru-accent-ink)',
    display: 'grid', placeItems: 'center', fontSize: 13, fontWeight: 800,
  }

  return (
    <div
      id={`getready.eventcard.${event.id}`}
      data-testid="getready-eventcard"
      data-event-id={event.id}
      data-closed={closed ? 'true' : undefined}
      style={{
        // Slate container boundary (measured, one colour on every card).
        background: 'var(--tru-surface-2)',
        border: 'var(--tru-slate-border)',
        borderRadius: 'var(--tru-slate-radius)',
        overflow: 'hidden',
        marginBlockEnd: 'var(--tru-card-gap)',
        boxShadow: 'var(--tru-slate-halo), var(--tru-slate-shadow)',
        // Closed card reads as archived — dimmed, badge above still legible.
        ...(closed ? { opacity: 0.6 } : {}),
      }}
    >
      {/* header — slate tint fill + slate rule */}
      <div style={{ padding: '14px 16px', background: 'var(--tru-slate-header-fill)', borderBlockEnd: 'var(--tru-slate-header-rule)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {event.organiser.name && (
              <div className="mono" style={{ fontSize: 9.5, letterSpacing: '.11em', textTransform: 'uppercase', color: kickerColor, marginBlockEnd: 3 }}>
                {event.organiser.name}
              </div>
            )}
            <div style={{ fontSize: 14.5, fontWeight: 800, lineHeight: 1.2 }}>{event.name}</div>
            {/* TICKET-TYPE line (Duncan UI order BOARDROOM-135429): WHICH
                ticket this card is — the holdings wire's ticketTypeName
                (e.g. "Singles Solo Pro"), raw wire data (no i18n copy).
                Secondary metadata voice; absent (older my-api / fixture) ⇒
                omitted honestly. Logical-props only — RTL-safe. */}
            {event.ticketTypeName && (
              <div
                data-testid="getready-card-tickettype"
                style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--tru-text-secondary)', marginBlockStart: 2 }}
              >
                {event.ticketTypeName}
              </div>
            )}
            {/* "For {name}" subject line — on EVERY card since BOARDROOM-
                135429 (was child-only): child seats show the wire-named
                child; every other holding shows the signed-in holder's OWN
                name (the data layer resolves it — session claims, the waiver
                prefill source). Rendered only when a subject is NAMED (never
                an invented name, never a blank "For "). */}
            {event.holderName && (
              <div
                data-testid="getready-card-subject"
                style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--tru-status-teal-bright)', marginBlockStart: 2 }}
              >
                {t('getReady.card.forName', { name: event.holderName })}
              </div>
            )}
            {event.when && <div style={{ fontSize: 11.5, color: 'var(--tru-text-tertiary)' }}>{event.when}</div>}
          </div>
          {/* status chip + `X of Y done` inline right — desktop only */}
          {!isMobile && (
            <>
              {pillNode}
              {/* Progress text is meaningless once the event has ended. */}
              {!closed && (
                <span data-testid="getready-card-pct" style={{ flex: 'none', whiteSpace: 'nowrap', fontSize: 11.5, color: 'var(--tru-text-tertiary)' }}>
                  {t('getReady.card.done', { done: doneN, yours: yoursN })}
                </span>
              )}
            </>
          )}
        </div>
        {/* mobile: chip wraps below the title; pct text hidden */}
        {isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBlockStart: 10 }}>{pillNode}</div>
        )}
        {/* 5px progress bar — track/fill from tokens (fills from the right
            automatically under direction: rtl) */}
        <div style={{ height: 'var(--tru-progress-h)', borderRadius: 'var(--tru-progress-radius)', background: 'var(--tru-progress-track)', overflow: 'hidden', marginBlockStart: 8 }}>
          <div data-testid="getready-card-progress" style={{ height: '100%', borderRadius: 'var(--tru-progress-radius)', background: 'var(--tru-progress-fill)', width: `${pct}%` }} />
        </div>
      </div>

      {/* steps — mono label, then ONE status-coloured bracket wrapping ALL
          step rows. The bracket has NO header row (QA 1.5) — the card header
          already names the event and its state. */}
      <div className="mono" style={{ fontSize: 9.5, letterSpacing: '.1em', color: 'var(--tru-text-muted)', padding: '12px 16px 2px' }}>
        {t('getReady.stepsLabel')}
      </div>
      <div style={{ padding: isMobile ? '6px 4px 10px' : '8px 14px 12px' }}>
        <div
          className="bracket"
          data-testid="getready-steps-bracket"
          style={{ '--bracket-color': bracketColor, overflow: 'hidden' } as CSSProperties}
        >
          {event.steps.map(step => (
            <StepRowView key={step.key} event={event} step={step} onAction={onAction} />
          ))}
        </div>
      </div>

      {/* PROFILE-TIME eligibility reject bar (BOARDROOM-2026-08-28-194055 item
          2): the SERVER's 422 'ineligible' from this ticket's profile-complete
          call, in plain locale copy — the amber blocked-reason bar pattern
          (state colour, never the teal reserve). Absent unless a reject is
          recorded AND the profile step is still open; suppressed on a closed
          card (nothing is actionable there). */}
      {/* ELIGIBILITY-MISMATCH banner + the out (BOARDROOM-2026-08-29-120259
          rulings 1–3 + addenda): when the COMPLETE profile fails this ticket's
          contract (gender OR age axis), the shared notice renders with its two
          sub-states (support button before reporting; "support request
          submitted" + reference after — no re-nag) and SUPERSEDES the bare
          profileBlock bar below (same fact, plus the resolution action).
          Waiver/questionnaire/heat rows above keep their CTAs — Duncan
          explicitly did NOT block them. Suppressed on a closed card. */}
      {/* keyed by the backing ticket (review P2-1): the notice seeds its
          reported sub-state from the per-ticket store ONCE on mount, so a
          LIVE ticket swap under the same card must re-key (remount) it —
          otherwise the old ticket's reported state renders against the new
          ticket. And when NO backing ticket is known, NO notice renders
          (P2-1 shared-''-key guard): a ''-keyed record would be shared by
          every ticketless card; the truthful blocked step row still shows. */}
      {!closed && event.eligibilityMismatch && ticketId && (
        <EligibilityMismatchNotice
          key={ticketId}
          ticketId={ticketId}
          eventName={event.name}
          copy={event.eligibilityMismatch.copy}
          reason={event.eligibilityMismatch.reason}
          participantName={event.eligibilityMismatch.participantName}
        />
      )}
      {!closed && !event.eligibilityMismatch && event.profileBlock && (
        <div
          data-testid="getready-card-eligblock"
          role="alert"
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px',
            background: 'rgba(230,193,90,.1)', borderBlockStart: '1px solid rgba(230,193,90,.28)',
          }}
        >
          <span aria-hidden style={{ flex: 'none', color: 'var(--tru-status-amber)', fontSize: 13 }}>⚠</span>
          <span style={{ fontSize: 11.5, lineHeight: 1.45, color: 'var(--tru-status-amber)', fontWeight: 700 }}>
            {event.profileBlock}
          </span>
        </div>
      )}

      {/* "You're up to date" band — nothing actionable but not finished
          (caught-up / yours-done). Carries the derived next-up line. A closed
          card suppresses it (nothing is coming next). */}
      {!closed && (state === 'caught-up' || state === 'yours-done') && (
        <div data-testid="getready-card-uptodate" style={bandStyle}>
          <span aria-hidden style={bandTickStyle}>✓</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--tru-status-done-text)' }}>{t('getReady.upToDate.title')}</div>
            <div data-testid="getready-card-nextup" style={{ fontSize: 11.5, color: 'var(--tru-text-tertiary)', marginBlockStart: 2, lineHeight: 1.4 }}>
              {t('getReady.upToDate.body')}{nextUp ? ` ${nextUp}` : ''}
            </div>
          </div>
        </div>
      )}

      {/* "You're all set" band (all-done only) */}
      {state === 'all-done' && (
        <div data-testid="getready-card-alldone" style={bandStyle}>
          <span aria-hidden style={bandTickStyle}>✓</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* all-done ⇔ EVERY step (incl. system) is done ⇒ the ticket IS
                issued and sitting in the wallet — so instead of the old
                dead-end "we'll take it from here", point the participant
                straight at it (BOARDROOM-2026-08-14-100859 UX #5). */}
            <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--tru-status-done-text)' }}>{t('getReady.allSet.title')}</div>
            <div style={{ fontSize: 11.5, color: 'var(--tru-text-tertiary)', marginBlockStart: 2, lineHeight: 1.4 }}>
              {t('getReady.allSet.body')}{' '}
              <Link
                data-testid="getready-card-alldone-cta"
                to="/tickets"
                style={{ color: 'var(--tru-status-teal-bright)', fontWeight: 700, textDecoration: 'underline' }}
              >
                {t('getReady.allSet.cta')}
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* footer note (canon §4.2). A server-provided note wins; otherwise, while
          the required heat step is still pending, the card derives the verbatim
          issuance line from its steps so the live pre-heat card always carries
          it (audit D4 — the live path never supplies a per-event note). A
          CLOSED event suppresses it — "issued once you choose your heat" is
          dead coaching after the event has ended. */}
      {!closed && (event.note ?? heatIssuanceNote(event, t)) && (
        <div data-testid="getready-card-note" style={{ padding: '11px 16px', borderBlockStart: '1px solid var(--tru-hairline-06)', fontSize: 11.5, color: 'var(--tru-text-quaternary)', lineHeight: 1.45 }}>
          {event.note ?? heatIssuanceNote(event, t)}
        </div>
      )}
    </div>
  )
}

/** Presentational card list (max-width 700px column, §4.1). Rendered by the
 *  live container below; unit tests render it with the seed FIXTURE. Cards
 *  are PER HOLDING (child-holder slice) — keyed `id|ticketId` because two
 *  same-event holdings (guardian's own seat + a child seat) both render; each
 *  card is backed by ITS OWN ticket, the event-keyed map is the fixture/legacy
 *  fallback only. */
export function GetReadyList({ events, ticketByEvent, onStepAction }: {
  events: GetReadyEvent[]
  /** eventId → backing held ticket (support pre-fill + reported-state key) —
   *  fallback when an event entry carries no ticketId of its own (fixtures). */
  ticketByEvent?: Record<string, string>
  onStepAction?: (event: GetReadyEvent, step: GetReadyStep) => void
}) {
  return (
    <div style={{ maxWidth: 'var(--content-max)' }}>
      {events.map(e => (
        <EventCard
          key={`${e.id}|${e.ticketId ?? ''}`}
          event={e}
          ticketId={e.ticketId ?? ticketByEvent?.[e.id]}
          onAction={onStepAction}
        />
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Live data — real held events + server-derived steps (Rulings A + C)
// ---------------------------------------------------------------------------

interface LiveGetReady {
  /** Distinct guard states with honest, non-conflated copy (audit D7):
   *  - 'unconfigured' → no VITE_MY_API_BASE (a genuine build gap → NotWired)
   *  - 'signed-out'   → no session, or a 401/403 mid-session (sign-in prompt)
   *  - 'unavailable'  → a network/5xx fetch failure (service not responding) */
  state: 'unconfigured' | 'signed-out' | 'loading' | 'ready' | 'unavailable'
  /** PER-HOLDING since the child-holder slice: one entry per held ticket
   *  (child seats included), each carrying its own ticketId/holderKind. */
  events: GetReadyEvent[]
  /** LEGACY eventId → held-ticket map for event-keyed consumers; prefers the
   *  account's OWN (non-child) holding. Card CTAs use the card's ticketId. */
  ticketByEvent: Record<string, string>
  /** Holdings this account USED TO have (transferred away / reassigned /
   *  CS-changed) — the ADDITIVE /me/holdings `removedHoldings[]` carry
   *  (build item #6, BOARDROOM-2026-09-05-121056). [] on an older my-api /
   *  any non-ready state — consumers (the Orders + Get-ready empty states)
   *  then behave exactly as before the field existed. */
  removedHoldings: RemovedHolding[]
}

/** A thrown my-api error that means the session is no longer valid (401/403)
 *  → maps to 'signed-out', never the "service unavailable" copy (audit D7). */
function isAuthFailure(err: unknown): boolean {
  return err instanceof MyApiError && (err.status === 401 || err.status === 403)
}

/** Exported for the Tickets wallet (canon §8): issuance derives from the SAME
 *  live source of truth as Get ready (§2.4 single-source rule / Ruling C). */
export function useLiveGetReadyEvents(): LiveGetReady {
  const { liveState, eventById, orders, ticketsForOrder, sessionEmail } = useData()
  // Step label/meta strings resolve through the live locale (`getReady.step.*`
  // keys); `lang` re-runs the derivation effect on a language switch so the
  // rendered steps re-resolve (rare; the readiness re-read is server truth).
  const { t, lang } = useLocale()
  const [out, setOut] = useState<LiveGetReady>({ state: 'unconfigured', events: [], ticketByEvent: {}, removedHoldings: [] })
  // A profile/waiver save bumps this via the readiness-refresh bus so the
  // subscription re-reads server readiness on return to Get ready — otherwise
  // the effect only re-runs on liveState/tickets changes and shows a stale
  // pre-save snapshot (CMT-043 live-test bug).
  const [refreshTick, setRefreshTick] = useState(0)
  useEffect(() => subscribeReadinessRefresh(() => setRefreshTick((t) => t + 1)), [])
  // Assign-store writes (a child rename via the managed wizard, a new local
  // seat record, a stale-record clear) re-derive the child-identity overlay +
  // waiting cards. The ticketId backfill stamp is a SILENT store write
  // (PR#124 review P1-1) — it never lands here, so a resolution pass can
  // never restart the read it is part of.
  useEffect(() => subscribeAssignStore(() => setRefreshTick((t) => t + 1)), [])

  useEffect(() => {
    const base = myApiBase()
    const session = loadSession()
    // Split the three distinct guard states (audit D7): no API base is a
    // genuine build gap; no session is a sign-out (never "build unwired").
    if (!base) {
      setOut({ state: 'unconfigured', events: [], ticketByEvent: {}, removedHoldings: [] })
      return
    }
    if (!session) {
      setOut({ state: 'signed-out', events: [], ticketByEvent: {}, removedHoldings: [] })
      return
    }
    if (liveState !== 'ready') {
      setOut({ state: 'loading', events: [], ticketByEvent: {}, removedHoldings: [] })
      return
    }
    let cancelled = false
    setOut(o => ({ ...o, state: 'loading' }))
    ;(async () => {
      // LOCAL SEAT-RECORD RESOLUTION (rulings items 2 + 5) — in parallel with
      // the holdings read. BEST-EFFORT: any failure resolves EMPTY (the cards
      // keep their pre-overlay honest rendering; no waiting cards) — the
      // overlay must never take the page down.
      const overlayPromise: Promise<ResolvedLocalRecords> = resolveLocalSeatRecords({
        idToken: session.idToken, base, orders, ticketsForOrder, sessionEmail,
      }).catch(() => ({ childByTicketId: new Map(), waiting: [] }))

      // Held places come SOLELY from my-api holdings (assign-cascade fix): a
      // card appears here only for a place the account EXPLICITLY holds — a
      // buyer's own self-assign, or an invitee's claim. Opening Get ready never
      // auto-claims purchased tickets (the removed ensureSelfHolder-over-all
      // loop). A holdings fetch failure is a genuine outage → the outer catch.
      // The SAME read also carries the additive removedHoldings[] (item #6,
      // BOARDROOM-2026-09-05-121056) — [] on an older my-api; it feeds the
      // Orders/Get-ready empty-state breadcrumbs only.
      const { holdings, removedHoldings } = await fetchHoldingsRead(session.idToken, base)
      const { childByTicketId, waiting } = await overlayPromise

      // CARD IDENTITY (Duncan UI order BOARDROOM-135429): the signed-in
      // holder's OWN full name for the "For {name}" line on self-held cards —
      // resolved from the verified session ID-token claims (given_name/
      // family_name, then name), the SAME source the waiver signature prefill
      // uses (auth/session.nameFromClaims). '' when the token carries no name
      // ⇒ the line is omitted honestly, never fabricated.
      const sessionName = nameFromClaims(session.claims)

      // TRUTHFUL-STATE inputs (BOARDROOM-2026-08-29-120259 ruling 3 +
      // ADDENDUM 2): the SAVED self profile decides whether "Details missing"
      // is even true. Loaded ONCE per refresh (the profile is account-wide);
      // completeness mirrors the wizard's own derivation (allValid + the
      // identity-lock rule — same inputs as resolveResumeStep). BEST-EFFORT:
      // any failure leaves attrs null → NO overlay is ever guessed, and the
      // server-truth "Details missing" stands (the pre-existing behaviour).
      let selfAttrs: ProfileMismatchAttrs | null = null
      try {
        const [draft, level] = await Promise.all([
          Promise.resolve(loadSelfDraft()).catch(() => null),
          Promise.resolve(loadIdentityVerificationLevel()).catch(() => 3 as const),
        ])
        if (draft) {
          selfAttrs = {
            gender: draft.gender,
            birthdate: dobIso(draft),
            complete: allValid(draft, DEFAULT_WIZARD_CONFIG.genderPolicy, (level ?? 3) <= 2),
            participantName: `${draft.first} ${draft.last}`.trim(),
          }
        }
      } catch {
        // attrs stay null — never a guessed overlay
      }

      // One card per HOLDING (child-holder slice) — keyed eventId|ticketId so
      // a guardian's own seat AND a child seat in the SAME event each get a
      // card (the old byEvent collapse hid one of them). Duplicate wire rows
      // for the same ticket (defensive) still collapse to one card.
      const byHolding = new Map<string, Holding>()
      for (const h of holdings) {
        const k = `${h.eventId}|${h.ticketId}`
        if (!byHolding.has(k)) byHolding.set(k, h)
      }

      // PERF (CMT-150431): ONE batched readiness call for every holding replaces
      // the per-ticket readiness N+1 that made large orders (~18 tickets) hang /
      // "not respond". Build a ticketId→readiness map. A 404 means an older
      // my-api WITHOUT /me/readiness → fall back to the per-holding fetchEventSteps
      // fan-out below (readinessByTicket stays null), so the FE degrades
      // gracefully until the my-api change deploys. Any OTHER error is a genuine
      // outage → rethrow to the outer catch (auth→signed-out, else unavailable).
      let readinessByTicket: Map<string, Readiness> | null = null
      if (byHolding.size > 0) {
        try {
          const all = await fetchAllReadiness(session.idToken, base)
          readinessByTicket = new Map(all.map((r) => [r.ticketId, r.readiness]))
        } catch (err) {
          // The batched endpoint is a PERF optimization ONLY: ANY failure — a 404
          // on an older my-api, a 500 (stage /me/readiness 500'd right after the
          // readiness deploy), a timeout — must degrade to the per-ticket fan-out
          // below and NEVER blank the whole Get-ready page. Only a genuine auth
          // failure propagates (→ signed-out). Previously this re-threw on any
          // non-404, so a batched 500 collapsed the entire page to "unavailable".
          if (isAuthFailure(err)) throw err
          readinessByTicket = null // batched unavailable → per-holding fallback
        }
      }

      // QUESTIONNAIRE DEDUP (the CMT-150431 N+1 lesson, reapplied): the read
      // is per-EVENT, but the card build below runs per-HOLDING — on a
      // many-ticket account the un-memoised inline fetch fired once PER
      // HOLDING (426 parallel GETs for ~5 events on the e2e fixture), enough
      // to exhaust the API-Gateway throttle by itself and 429-starve every
      // other my-api call on the page (the 2026-09-03 empty-page P1). One
      // shared in-flight promise per unique eventId; best-effort semantics
      // (.catch → null → step absent) unchanged.
      const questionnaireByEvent = new Map<string, Promise<QuestionnaireStatus | null>>()
      const questionnaireFor = (eventId: string): Promise<QuestionnaireStatus | null> => {
        let p = questionnaireByEvent.get(eventId)
        if (!p) {
          p = fetchQuestionnaire(session.idToken, base, eventId).catch(() => null)
          questionnaireByEvent.set(eventId, p)
        }
        return p
      }

      // RESILIENCE (STEP 4): build every event's card IN PARALLEL and let a
      // single slow/failed per-event read degrade to skipping THAT card only —
      // one failing/slow sub-call must never blank the whole page (Duncan's
      // ~35s-hang collapse). In the batched path the readiness is already in
      // hand (no per-card round-trip); only a ticket missing from the batch (a
      // rare read race) or the 404 fallback re-fetches via fetchEventSteps. The
      // readiness→steps derivation is IDENTICAL either way (stepsFromTicketReadiness
      // + heatPolicyFromReadiness — same as fetchEventSteps), so behaviour is
      // unchanged, just fewer round-trips.
      const results = await Promise.allSettled(
        [...byHolding.values()].map(async (h): Promise<{ event: GetReadyEvent; ticketId: string }> => {
          const ev = eventById(h.eventId)
          const batched = readinessByTicket?.get(h.ticketId)
          // QUESTIONNAIRE step (SETTINGS-2026-08-25-105204 via the my-api BFF,
          // contract MY-2026-08-25-054534): per-EVENT read, fetched in parallel
          // with the (fallback) readiness read. BEST-EFFORT: any failure — a
          // 404 on an older my-api without the BFF, a 502 (settings /svc down)
          // — resolves null and the step is simply ABSENT (never a fabricated
          // row, never a blanked card).
          const [stepData, questionnaire] = await Promise.all([
            batched
              ? Promise.resolve({
                  steps: stepsFromTicketReadiness(batched, {
                    organiserName: ev?.organiser.name,
                    heatPolicy: heatPolicyFromReadiness(batched),
                    tr: t,
                  }),
                  eventStartsAt: batched.eventStartsAt ?? null,
                  selectedHeatTime: batched.selectedHeatTime ?? null,
                  // POST-EVENT CLOSE (BOARDROOM-2026-08-25-044751): SERVER-computed
                  // — absent on an older my-api readiness payload ⇒ open.
                  eventClosed: batched.eventClosed ?? false,
                  // Past-event partition carry (BOARDROOM-2026-09-01-175306
                  // item 2) — absent (older my-api) = null = never excluded.
                  eventEndsAt: batched.eventEndsAt ?? null,
                  // ELIGIBILITY CARRY (BOARDROOM-2026-08-28-194055 item 4):
                  // feeds the reject-copy mapping below; absent = null.
                  eligibility: batched.eligibility ?? null,
                  raceDay: batched.raceDay ?? null,
                })
              : fetchEventSteps(session.idToken, base, h.ticketId, { organiserName: ev?.organiser.name, tr: t }),
            questionnaireFor(h.eventId),
          ])
          const { eventStartsAt, selectedHeatTime, eventClosed, eventEndsAt } = stepData
          // Renders IFF active && show && !submitted (submitted → done row;
          // otherwise absent) — questionnaireStepFromStatus inside the helper.
          const steps = withQuestionnaireStep(stepData.steps, questionnaire, t)
          // PROFILE-TIME eligibility reject (BOARDROOM-2026-08-28-194055 item
          // 2): a recorded 422 from this ticket's profile-complete call
          // surfaces on the card — locale-mapped via the machine reason + the
          // in-hand contract, else the server's own plain message. Shown ONLY
          // while the SERVER still reports the profile step incomplete (a
          // successful flip or a fresh readiness read saying done wins).
          const rejection = profileEligibilityRejection(h.ticketId)
          const profileStillOpen = steps.some((s) => s.key === 'profile' && s.state !== 'done')
          // GUARDIAN-CLAIMED seat (S2 slice, BOARDROOM-2026-09-03-052111 /
          // CMT-2026-09-03-020): the device-local claim record. Resolved here
          // because BOTH eligibility surfaces below are suppressed on a
          // guardian seat — the PARENT's profile is account-holder data,
          // never validated against the CHILD's ticket contract (Duncan's
          // live-walk defect: the parent's adult DOB warned against the child
          // ticket's 13–15 band with a support CTA).
          // STALE-RECORD GUARD (the PR#124 P1-2 pattern, applied to the
          // guardian store): an EXPLICIT kind='self' holding is the wire
          // saying "this seat is the caller's own" (only ensureSelfHolder
          // mints it; guardian-claimed seats ride invitee/child) — a
          // lingering claim record for that row is stale (revoke-then-self-
          // reassign) and must never flip a self seat to guardian treatment.
          const guardianRec = h.kind === 'self' ? null : guardianClaimForTicket(h.ticketId)
          // DECLARED-PERMISSION managed seat (dispatch BOARDROOM-2026-09-07-
          // 103021, Duncan-settled): a seat the account holder manages for a
          // child under an assign-time declared permission — the wire's
          // kind='child', or a local assign-store child record with a managed
          // profile key (the SAME predicate the step routing uses, so the
          // composition and Edit targets never disagree). The kind='self'
          // stale-record guard above already zeroed localChild on self seats.
          const localChild = h.kind === 'self' ? null : childByTicketId.get(h.ticketId) ?? null
          const managedChildKey = guardianRec ? null : localChild?.childKey || null
          const managedChildSeat = !guardianRec && (h.kind === 'child' || !!managedChildKey)
          // Both eligibility surfaces below are suppressed on a managed child
          // seat for the SAME reason as on a guardian seat: the PARENT's
          // account profile is never validated against the CHILD's ticket
          // contract (the guardian live-walk defect — an adult DOB warned
          // against a kids band). Suppression only; nothing new is gated.
          const profileBlock = rejection && profileStillOpen && !guardianRec && !managedChildSeat
            ? profileBlockCopy(rejection, stepData.eligibility, t)
            : undefined
          // ELIGIBILITY-MISMATCH overlay (rulings 1–3 + addenda): a COMPLETE
          // saved profile that fails THIS ticket's carried contract (gender OR
          // age axis) — or whose profile-complete 422 was recorded — renders
          // the truthful blocked step ("Complete — not valid for this ticket")
          // + the card banner with the support out, INSTEAD of the untruthful
          // "Details missing". Derived per render, per ticket; a closed event
          // keeps its archived read (no overlay). Completion state is never
          // touched — a support-side ticket swap finds profile/waiver/
          // questionnaire already complete (ADDENDUM zero-rework).
          const mismatch = eventClosed || guardianRec || managedChildSeat
            ? null
            : detectProfileMismatch({
                steps,
                eligibility: stepData.eligibility,
                raceDay: stepData.raceDay,
                attrs: selfAttrs,
                rejection,
                tr: t,
              })
          const mismatchSteps = mismatch ? applyProfileMismatch(steps, t) : steps
          // Guardian journey rows (applyGuardianClaim): childConfirm inserted,
          // the account-holder profile meta, the guardian waiver framing, and
          // the who-is-this-about label markers on EVERY row — the profile
          // names the PARENT, child-facing rows name the child (BOARDROOM-
          // 2026-09-05-101136, Duncan's "Your profile = whose?" live test).
          // DECLARED-PERMISSION managed seats get the SAME two-identity
          // composition (dispatch BOARDROOM-2026-09-07-103021 — the card's
          // "Your profile / Sign your waiver" under a "For {child}" header
          // read as the PARENT's own steps): the child-details row is
          // subject-keyed to the managed wizard, and its done-state is the
          // LOCAL child profile's identity completeness (name + DOB + gender
          // — the facts resolveChildResumeStep walks; my-api has no
          // child-details read, so this is honestly device-local, exactly
          // like the guardian record's confirmed flag).
          const finalSteps = guardianRec
            ? applyGuardianClaim(mismatchSteps, guardianRec, t)
            : managedChildSeat
              ? applyDeclaredPermissionChild(
                  mismatchSteps,
                  {
                    childName: h.kind === 'child'
                      ? (h.holderName ?? '').trim() || localChild?.name || ''
                      : localChild?.name ?? '',
                    confirmed: managedChildKey ? resolveChildResumeStep(managedChildKey) > 2 : false,
                    childKey: managedChildKey,
                  },
                  t,
                )
              : mismatchSteps
          // LOCAL child-record overlay for THIS ticket row (ruling item 2 —
          // the "For Duncan Francis" defect shapes b/c): a locally-recorded
          // child assignment names the CHILD even when the wire cannot.
          // STALE-RECORD GUARD (PR#124 review P1-2): an EXPLICIT kind='self'
          // holding is the wire saying "this seat is the caller's own" (only
          // ensureSelfHolder mints it) — a lingering child record for that
          // row is stale (revoke-then-self-reassign) and must not overlay a
          // child identity or route Edit into the child wizard.
          // (localChild itself is resolved above, before the step overlays.)
          return {
            event: {
              id: h.eventId,
              // Per-holding card identity (child-holder slice + the
              // BOARDROOM-135429 every-card identity lines): the backing
              // ticket, the wire's subject carry, and the ticket TYPE.
              ticketId: h.ticketId,
              holderKind: h.kind,
              // Card subject — ruling precedence: wire holderName
              // (kind='child') > local child-record name > session self-name.
              // A ticket locally recorded as a CHILD's NEVER falls back to
              // the guardian's session name: an unnamed child record omits
              // the For line honestly (null), it does not borrow the adult.
              // GUARDIAN-CLAIMED seat (S2 slice): the subject is the CHILD —
              // the local claim record's name (the parent's own confirmed
              // input outranks the wire seed), else the wire's child name;
              // NEVER the parent's session name (child named by the CHILD
              // everywhere — the "card reads the PARENT's name" defect).
              holderName: guardianRec
                ? guardianRec.childName || (h.holderName ?? '').trim() || null
                : h.kind === 'child'
                  ? (h.holderName ?? '').trim() || localChild?.name || null
                  : localChild
                    ? (localChild.name || null)
                    : (h.holderName ?? '').trim() || sessionName || null,
              // Managed-profile key for Edit-follows-the-holder (item 3).
              // NOT on a guardian-claimed seat: its profile step is the
              // PARENT's own account wizard (account-holder data), never a
              // managed-child wizard route.
              childKey: managedChildKey,
              // Guardian-journey marker for the card + step routing.
              ...(guardianRec
                ? { guardianClaim: { childName: guardianRec.childName, confirmed: guardianRec.confirmed } }
                : {}),
              // TYPE line — already on the /me/holdings wire; ''/absent
              // (older my-api) ⇒ null ⇒ omitted honestly.
              ticketTypeName: (h.ticketTypeName ?? '').trim() || null,
              name: ev?.name ?? h.eventName,
              // Org accent comes from the central Trumin asset store per
              // org-unit in production (V1.9.1: DATA-driven per org unit).
              // '' when the wire carries none — the card view applies a
              // NEUTRAL fallback, never a status hue (README §Status colours).
              organiser: { name: ev?.organiser.name ?? '', accentFg: ev?.organiser.accentFg ?? '' },
              when: ev?.when ?? '',
              eventStartsAt,
              selectedHeatTime,
              eventClosed,
              eventEndsAt,
              // The mismatch notice supersedes the bare profileBlock bar
              // (same fact + the resolution action) — both are never shown.
              ...(mismatch ? { eligibilityMismatch: mismatch } : profileBlock ? { profileBlock } : {}),
              steps: finalSteps,
            },
            ticketId: h.ticketId,
          }
        }),
      )
      const okResults = results.filter(
        (r): r is PromiseFulfilledResult<{ event: GetReadyEvent; ticketId: string }> => r.status === 'fulfilled',
      )
      // Every per-event read failed while we DID hold places — surface the
      // honest outage/sign-in state (via the outer catch: auth→signed-out, else
      // unavailable) rather than a misleading "nothing to get ready for" empty.
      if (byHolding.size > 0 && okResults.length === 0) {
        throw (results.find(r => r.status === 'rejected') as PromiseRejectedResult).reason
      }
      // Chronological order — the soonest-arriving event at the TOP so the user
      // is drawn to the one they most likely need to get ready for first
      // (CMT-121335) — with CLOSED events SUNK to the bottom regardless of date
      // (BOARDROOM-2026-08-25-044751: an ended event's start is the earliest in
      // the list, so pure chronology pinned it to the top — the inversion this
      // fixes). Shared comparator (compareGetReadyEvents) so the wallet order
      // provably agrees.
      // PARENT-SIGNING waiting cards (ruling item 5): one read-only card per
      // guestparent seat whose wire holder is live. A ticket the caller ALSO
      // holds (edge: the invite was claimed by the buyer's own account) keeps
      // its real holding-backed card instead — never two cards for one row.
      const heldTicketIds = new Set(holdings.map((h) => h.ticketId))
      const waitingEvents: GetReadyEvent[] = waiting
        .filter((w) => !heldTicketIds.has(w.ticketId))
        .map((w) => {
          const ev = eventById(w.eventId)
          return {
            id: w.eventId,
            ticketId: w.ticketId,
            // The card subject is the CHILD (item 2 applies here too); an
            // unnamed record omits the line — never the parent as subject.
            holderName: w.childName || null,
            ticketTypeName: w.ticketTypeName,
            name: ev?.name ?? w.eventName,
            organiser: { name: ev?.organiser.name ?? '', accentFg: ev?.organiser.accentFg ?? '' },
            when: ev?.when ?? '',
            eventStartsAt: w.eventStartsAt,
            eventEndsAt: w.eventEndsAt,
            waiting: {
              parentName: w.parentName, parentEmail: w.parentEmail, state: w.state,
              qrAvailable: w.qrAvailable,
              // Purchaser-scoped QR routes are order-addressed (PR#125).
              orderId: w.orderId,
            },
            // NO step rows: the buyer cannot read the parent's readiness —
            // nothing is fabricated. isIssued() guards the vacuous
            // every()=true so this card never counts issued.
            steps: [],
          }
        })
      const events: GetReadyEvent[] = [...okResults.map(r => r.value.event), ...waitingEvents]
        .sort(compareGetReadyEvents)
      // LEGACY per-event backing map, kept for the consumers that are still
      // event-keyed (Tickets wallet fallback, WaiverFlow's holdings-less
      // resolution). Since cards went PER-HOLDING it prefers the account's
      // OWN (non-child) holding for an event — the pre-slice value — so an
      // event-keyed read never silently lands on the child's ticket; card
      // CTAs never use it (they route with their card's own ticketId).
      const ticketByEvent: Record<string, string> = {}
      const ticketByEventKind: Record<string, string | undefined> = {}
      for (const r of okResults) {
        const evt = r.value.event
        if (!(evt.id in ticketByEvent) || (ticketByEventKind[evt.id] === 'child' && evt.holderKind !== 'child')) {
          ticketByEvent[evt.id] = r.value.ticketId
          ticketByEventKind[evt.id] = evt.holderKind
        }
      }
      if (!cancelled) setOut({ state: 'ready', events, ticketByEvent, removedHoldings })
    })().catch(err => {
      if (cancelled) return
      // A 401/403 mid-session is an expired/invalid session, not an outage
      // (audit D7) — show the sign-in prompt, not "service not responding".
      setOut({ state: isAuthFailure(err) ? 'signed-out' : 'unavailable', events: [], ticketByEvent: {}, removedHoldings: [] })
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveState, refreshTick, lang])

  return out
}

// ---------------------------------------------------------------------------
// Shared live provider (audit D8) — one subscription the SHELL badge and the
// Get-ready screen both read, so the nav badge derives from the SAME live
// non-system `now`-step count as the card pills (canon §2.4). Lifting it above
// AppShell is what lets the shell render a badge the screen data drives.
// ---------------------------------------------------------------------------

const GetReadyLiveContext = createContext<LiveGetReady | null>(null)

/** Provides one shared `useLiveGetReadyEvents()` result. Placed above AppShell
 *  (inside DataProvider) so both the shell badge and the screen consume it. */
export function GetReadyLiveProvider({ children }: { children: ReactNode }) {
  const live = useLiveGetReadyEvents()
  return <GetReadyLiveContext.Provider value={live}>{children}</GetReadyLiveContext.Provider>
}

/** Read the shared live Get-ready result. MUST be used within a
 *  GetReadyLiveProvider (App wraps the shell; tests wrap the screen). */
export function useGetReadyLive(): LiveGetReady {
  const ctx = useContext(GetReadyLiveContext)
  if (!ctx) throw new Error('useGetReadyLive must be used within a GetReadyLiveProvider')
  return ctx
}

/** Non-throwing reader for callers rendered under the provider in production but
 *  standalone in tests (e.g. WaiverFlow deriving the receipt's next action).
 *  Returns null outside a provider rather than throwing. */
export function useGetReadyLiveOptional(): LiveGetReady | null {
  return useContext(GetReadyLiveContext)
}

/** Honest signed-out / expired-session guard (audit D7): a signed-out user is
 *  NEVER told the build is unwired — they see a sign-in prompt + affordance. */
function SignInNotice() {
  const { t } = useLocale()
  const [busy, setBusy] = useState(false)
  return (
    <div
      role="status"
      data-testid="getready-signin"
      style={{
        border: '1px solid var(--hairline-12)', borderRadius: 'var(--r-card, 16px)',
        padding: 20, marginTop: 8, maxWidth: 'var(--content-max)',
        color: 'var(--text-secondary)', background: 'var(--surface)', fontSize: 13,
      }}
    >
      <p style={{ margin: '0 0 12px' }}>{t('getReady.signin.prompt')}</p>
      <button
        data-testid="getready-signin-cta"
        onClick={() => { setBusy(true); void startSignIn().then(started => { if (!started) setBusy(false) }) }}
        disabled={busy}
        style={{
          minHeight: 'var(--tap-min)', borderRadius: 9, padding: '0 16px', border: 0,
          background: 'var(--accent)', color: 'var(--accent-ink)', fontFamily: 'inherit',
          fontSize: 13, fontWeight: 800, cursor: 'pointer',
        }}
      >
        {/* Shared sign-in CTA copy (tickets.signin.* — one voice). */}
        {busy ? t('tickets.signin.busy') : t('tickets.signin.cta')}
      </button>
    </div>
  )
}

/** Resume opens the wizard at the first incomplete step (`pAt` — canon §4.3
 *  E2E contract), resolved from the subject's real stored profile PLUS the
 *  identity-verification level (review P2-4): the wizard's own resume passes
 *  `identityLocked` into firstIncompleteStep, so this entry must agree — at
 *  level <= 2 the locked steps 1–2 count as answered and resume lands on
 *  step 3, never on a read-only step. Same fail-open level read as the wizard
 *  (loadIdentityVerificationLevel resolves 3 on any failure). Exported for the
 *  parity test only. */
export async function resolveResumeStep(): Promise<number> {
  try {
    const [draft, level] = await Promise.all([loadSelfDraft(), loadIdentityVerificationLevel()])
    return firstIncompleteStep(draft ?? EMPTY_DRAFT, DEFAULT_WIZARD_CONFIG.genderPolicy, level <= 2)
  } catch {
    return 1
  }
}

/** CHILD-subject resume (Edit-follows-the-holder, ruling item 3): first
 *  incomplete step of THE CHILD's stored profile — the saved `child_{key}`
 *  draft when one exists, else the identity facts mirrored on the ChildRecord
 *  (the same seed the child wizard opens with). Identity lock never applies
 *  to a managed child (it is a verification level on the ACCOUNT's identity).
 *  Exported for the routing tests. */
export function resolveChildResumeStep(childKey: string): number {
  try {
    const rec = listChildren().find((c) => c.key === childKey)
    const draft = getChildProfile(childKey)
      ?? (rec
        ? { ...EMPTY_DRAFT, first: rec.first, last: rec.last, dobD: rec.dobD, dobM: rec.dobM, dobY: rec.dobY, gender: rec.gender }
        : EMPTY_DRAFT)
    return firstIncompleteStep(draft, DEFAULT_WIZARD_CONFIG.genderPolicy, false)
  } catch {
    return 1
  }
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

export function GetReady() {
  const { liveState } = useData()
  // NOTE: the live context's removedHoldings carry stays (Orders consumes it
  // through useGetReadyLiveOptional) — this screen just no longer renders it
  // (review fix F4: the CMT-034 redirect is unconditional again).
  const { state, events, ticketByEvent } = useGetReadyLive()
  // PAST-EVENT EXCLUSION (BOARDROOM-2026-09-01-175306 item 2): settled past
  // events (ends_at + 72h grace behind now — the ONE shared boundary in
  // src/data/pastEvents.ts) are excluded from the get-ready home entirely; a
  // missing eventEndsAt (older my-api / degraded read) keeps the event
  // visible. Their orders stay viewable read-only under Orders → Past events.
  const visibleEvents = visibleGetReadyEvents(events)
  const { showPageHead } = useShellChrome()
  const { t } = useLocale()
  const navigate = useNavigate()

  const onStepAction = (event: GetReadyEvent, step: GetReadyStep) => {
    void (async () => {
      // EDIT FOLLOWS THE TICKET HOLDER (Duncan ruling BOARDROOM-2026-09-02-
      // 150714 item 3): the profile/emergency steps on a CHILD-subject card
      // open THAT CHILD's managed wizard — NEVER the account owner's. The
      // childKey comes off the local-record overlay; a child-subject card
      // with no local record (assigned from another device — the child
      // profile isn't on this one) routes to the managed-profiles list
      // instead. The child flow is never event-scoped (ProfileWizard).
      // GUARDIAN-CLAIMED seat (S2 slice, BOARDROOM-2026-09-03-052111 /
      // CMT-2026-09-03-020): its profile step is the PARENT's OWN account
      // wizard (account-holder data — `&guardian=1` swaps the step-1 copy),
      // never a managed-child route — so the child-subject branch below is
      // skipped; the CHILD's identity lives in the childConfirm step.
      const guardianSeat = !!event.guardianClaim
      const childSubject = event.holderKind === 'child' || !!event.childKey
      // DECLARED-PERMISSION managed seat (dispatch BOARDROOM-2026-09-07-
      // 103021): the two-identity routing. The PROFILE row is the PARENT's
      // OWN account wizard in account-holder framing (&guardian=1) — the
      // reported routing bug had the "Your profile" Edit opening the CHILD's
      // name wizard; the child's details now live on the childConfirm row,
      // which edits THAT CHILD's managed wizard (item 3's edit-follows-the-
      // holder, moved to the step that actually names the child). The
      // emergency row stays the child's (wizard step 4, ruling item 3).
      const managedSeat = !guardianSeat && childSubject
      if (managedSeat && (step.key === 'childConfirm' || step.key === 'emergency')) {
        if (!event.childKey) {
          // No local child profile on this device → the managed list (never
          // the owner's wizard, never the guardian-claim child-confirm flow).
          navigate('/profile')
          return
        }
        if (step.key === 'emergency') {
          navigate(`/profile/children/${event.childKey}/step-4`)
          return
        }
        // childConfirm falls through: stepEntryPath routes it via the row's
        // subject key to /profile/children/{childKey}/step-1.
      }
      const resumeStep = step.key === 'profile' && step.cta === 'Resume' ? await resolveResumeStep() : undefined
      const path = stepEntryPath(event.id, step, { resumeStep, guardian: guardianSeat || managedSeat })
      if (!path) return
      // Waiver/heat surfaces need the backing ticket; carried as route state
      // until the canonical flows own their own lookups. THIS CARD's own
      // ticket (child-holder slice) — never the event-keyed map, which on a
      // guardian-own + child same-event pair would cross the two holdings.
      // holderKind/holderName ride along so the waiver flow can resolve its
      // guardian subject without a fresh holdings read. A guardian-claimed
      // seat rides holderKind 'child' explicitly: the waiver flow's guardian
      // subject ("Sign as guardian", participant = the child, signer = the
      // parent) keys on it, and the wire's own kind may not carry it —
      // truthful here: the seat IS the child's (the server preserves the
      // child as participant on guardian rows, S1 contract).
      navigate(path, {
        state: {
          ticketId: event.ticketId ?? ticketByEvent[event.id],
          eventName: event.name,
          // A managed (declared-permission) seat rides holderKind 'child' the
          // same way a guardian-claimed seat does: the waiver/questionnaire
          // flows key their guardian-subject framing on it (child = subject,
          // parent = signer) and the local record's seat may ride the wire as
          // invitee/kind-absent — truthful here: the seat IS the child's.
          holderKind: (guardianSeat || managedSeat) && event.holderName ? 'child' : event.holderKind,
          holderName: event.holderName ?? null,
          ...(step.key === 'childConfirm'
            ? { childName: event.guardianClaim?.childName ?? null }
            : {}),
        },
      })
    })()
  }

  return (
    <div id="getready.root">
      {/* Desktop-only page head (§4.1) */}
      {showPageHead && (
        <div id="getready.head" data-testid="getready-head">
          {/* Page h1 — 24px/800, −0.02em (V1.9.1 typography table). */}
          <h1 style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', margin: '0 0 4px' }}>{t('getReady.head.title')}</h1>
          {/* Verbatim [chrome] sub (canon §4.1). */}
          <p style={{ fontSize: 13, color: 'var(--tru-text-secondary)', margin: '0 0 16px' }}>
            {t('getReady.head.sub')}
          </p>
        </div>
      )}
      {state === 'signed-out' ? (
        // Honest sign-out copy — checked FIRST so a signed-out / expired user
        // is never shown the orders "not connected in this build" panel or the
        // my-api NotWired panel (audit D7).
        <SignInNotice />
      ) : liveState !== 'ready' ? (
        <LiveDataNotice liveState={liveState} />
      ) : state === 'unconfigured' ? (
        <NotWired service="The get-ready allocation/readiness service (my-api)" />
      ) : state === 'loading' ? (
        <p className="secondary" style={{ fontSize: 13 }} role="status">{t('getReady.loading')}</p>
      ) : state === 'unavailable' ? (
        <p className="secondary" role="alert" style={{ fontSize: 13 }}>
          {t('getReady.unavailable')}
        </p>
      ) : visibleEvents.length === 0 ? (
        // CMT-2026-08-14-034 (RESTORED by review fix F4): no held places ⇒
        // nothing to get ready for. Don't dead-end the purchaser on an empty
        // panel — send them to Orders, where their unassigned purchases live
        // and the next action (assign) actually is. The redirect is
        // UNCONDITIONAL again: the brief removed-holdings in-place variant is
        // gone — Orders' own empty state carries the "Recent changes"
        // breadcrumb (it consumes this provider's removedHoldings carry), and
        // a purchaser with unassigned orders lands back at the assign action.
        // Guarded to the CONFIRMED-empty ready state only (the signed-out /
        // unconfigured / loading / unavailable branches are handled above), so
        // a slow or down my-api never mis-redirects. `replace` keeps the back
        // button from bouncing straight back into this redirect.
        <Navigate to="/orders" replace />
      ) : (
        <GetReadyList events={visibleEvents} ticketByEvent={ticketByEvent} onStepAction={onStepAction} />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Flow-entry routes (temporary wrappers)
// ---------------------------------------------------------------------------

/** `/get-ready/heat/:eventId` — NON-CANON temporary route for the
 *  participant-chosen heat step (see the deviation note in
 *  src/data/getReady.ts stepEntryPath): the canon models heat allocation as
 *  system-automatic, today's my-api requires a choice. Removed when the
 *  server-derived step payload settles the conflict. */
export function HeatEntry() {
  const navigate = useNavigate()
  const location = useLocation()
  const { eventId } = useParams()
  const routeState = (location.state ?? {}) as { ticketId?: string; eventName?: string }
  const base = myApiBase()
  const session = loadSession()
  if (!routeState.ticketId || !base || !session || !eventId) return <Navigate to="/get-ready" replace />
  return (
    <HeatSelect
      eventName={routeState.eventName ?? 'Your event'}
      eventId={eventId}
      idToken={session.idToken}
      base={base}
      ticketId={routeState.ticketId}
      onBack={() => navigate('/get-ready')}
      onAssigned={() => navigate('/get-ready')}
    />
  )
}

/** `/get-ready/child-confirm/:eventId` — the guardian-claimed seat's
 *  child-identity step (S2 slice, BOARDROOM-2026-09-03-052111 /
 *  CMT-2026-09-03-020). Same wrapper shape as HeatEntry: the backing ticket
 *  rides in route state from the card's childConfirm CTA; a reload/deep link
 *  without it returns to the home (the card re-routes with fresh state). */
export function ChildConfirmEntry() {
  const navigate = useNavigate()
  const location = useLocation()
  const { eventId } = useParams()
  const routeState = (location.state ?? {}) as { ticketId?: string; eventName?: string }
  const base = myApiBase()
  const session = loadSession()
  if (!routeState.ticketId || !base || !session || !eventId) return <Navigate to="/get-ready" replace />
  return (
    <ChildConfirm
      eventName={routeState.eventName ?? 'Your event'}
      idToken={session.idToken}
      base={base}
      ticketId={routeState.ticketId}
      onBack={() => navigate('/get-ready')}
      onDone={() => navigate('/get-ready')}
    />
  )
}

/** `/get-ready/questionnaire/:eventId` — event-questionnaire flow entry
 *  (SETTINGS-2026-08-25-105204 via the my-api BFF, MY-2026-08-25-054534). Same
 *  wrapper shape as HeatEntry, minus the ticket: the questionnaire is keyed on
 *  event + verified caller (the BFF forwards the Cognito sub server-side), so
 *  no backing ticketId is needed. */
export function QuestionnaireEntry() {
  const navigate = useNavigate()
  const location = useLocation()
  const { eventId } = useParams()
  const routeState = (location.state ?? {}) as { eventName?: string }
  const base = myApiBase()
  const session = loadSession()
  if (!base || !session || !eventId) return <Navigate to="/get-ready" replace />
  return (
    <QuestionnaireFlow
      eventName={routeState.eventName ?? 'Your event'}
      eventId={eventId}
      idToken={session.idToken}
      base={base}
      onBack={() => navigate('/get-ready')}
      onDone={() => navigate('/get-ready')}
    />
  )
}
